/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { faArrowLeft, faCopy, faList, faPaste, faPlus, faTrash } from "@fortawesome/pro-regular-svg-icons";
import { useIsMutating } from "@tanstack/react-query";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { isArtLocation } from "../../../../../actions/Layers/constants";
import useConfig from "../../../../../actions/Tenants/config/configHook";
import { path_advance_ripening, path_summary } from "../../../../../actions/Tenants/config/constants";
import { useFruitImages } from "../../../../../hooks/useFruitImages";
import { isFilledArray } from "../../../../../utils/getFilledArrayOrDefault";
import { validateRequiredImages } from "../../../../../utils/imageValidation";
import { metaFormIsValid } from "../../../../Forms/MetaForm";
import { Button } from "../../../../Helper/Button";
import { useQcEntities } from "../../QcEntityContext";
import useCheckFlowNavigate from "../../useCheckFlowNavigate";
import { InvalidFruitModal } from "../InvalidFruitModal";

type ManualNavButtonsProps = {
    avocado: any;
    useGrid: boolean;
    useCopyPasteModal: boolean;
    addFruit(): void;
    toggleBulkCopyPasteModal(isOpen: boolean): void;
    duplicateFruit(): void;
}

export const ManualNavButtons = ({ avocado, addFruit, useGrid, useCopyPasteModal, toggleBulkCopyPasteModal, duplicateFruit }: ManualNavButtonsProps) => {
    const { check, layer } = useQcEntities();
    // No mutation dispatches avocados.isUpdating anymore; a pending fruit mutation is the equivalent signal.
    const isUpdating = useIsMutating() > 0;
    const params = useParams();
    const { navigateToCheckAction } = useCheckFlowNavigate();
    const [isInvalidModalOpen, toggleInvalidModal] = useState(false);
    const config = useConfig();
    const the_summary_path = isArtLocation(check.location) ? path_advance_ripening : path_summary;
    const prev = config.prev_check_page(layer, check, params.action as string);
    const next = config.next_check_page(layer, check, params.action as string);
    const location = config.get_check_type_config(layer, check);
    const unitOfAccount = config.get_check_unit_of_account(layer, check) || "Fruit";
    const fields = config.get_fruit_form(layer, check, avocado);

    // Get all fruit_images fields to extract required images from options
    // Multiple fruit-images fields can be configured in a single form
    const fruitImagesFields = fields.filter((f: any) => f.type === "fruit-images");

    // Merge required images from all fruit-images fields
    // If fields are hidden by display_filter, options will be empty
    const required_fruit_images = fruitImagesFields.flatMap((f: any) => f?.options || []);

    const currentFlow = location?.flow?.find((f) => f.path === params.action);
    const flowHasRequiredImages = !currentFlow?.disable_required_images;

    // Fetch fruit images for validation using TanStack Query
    const { data: images = [] } = useFruitImages(avocado.tenant_id, avocado.avocado_id);

    const isNextSummary = next && (next.path === the_summary_path);

    const onSummaryButtonClick = () => {
        const formValid = !isFilledArray(fields) || metaFormIsValid(fields, avocado, true);
        const imagesValid = !flowHasRequiredImages || validateRequiredImages(required_fruit_images, images || [], true);

        if (formValid && imagesValid) {
            navigateToCheckAction(`${the_summary_path}`);
        }
    };

    const onNextButtonClick = () => {
        if (next) {
            navigateToCheckAction(next.path);
        }
    };

    const onAddFruitButtonClick = () => {
        if (!isFilledArray(fields) || avocado.avocado_id === null || metaFormIsValid(fields, avocado, true)) {
            addFruit();
        }
    };

    return (
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
            {check.avocados.length > 0 && !useGrid && (
                <div className="me-auto d-flex gap-2 flex-1" css={css`@media (min-width: 768px) { max-width: 45%; }`}>
                    {avocado.avocado_id > 0 && (
                        <span className="pe-2" css={css`border-right: 1px solid #ddd; height: fit-content;`}>
                            <Button color="danger" outline onClick={() => toggleInvalidModal(true)} icon={faTrash} />
                        </span>
                    )}

                    <InvalidFruitModal
                        isOpen={isInvalidModalOpen}
                        index={check.avocados.findIndex((i) => i.avocado_id === avocado.avocado_id)}
                        toggleModal={toggleInvalidModal}
                        avocado={avocado}
                    />

                    <div className="d-flex flex-wrap gap-1">
                        {check.avocados.map((a, index) => (
                            <Button
                                key={a.avocado_id}
                                color={avocado.avocado_id === a.avocado_id ? "primary" : "secondary"}
                                // TODO: CHECK LATER WITH FLORIAN IF THIS WORKS
                                onClick={() => navigateToCheckAction(`${params.action}/${a.avocado_id}`)}
                                css={css`min-width: 40px;`}
                            >
                                {index + 1}
                            </Button>
                        ))}
                    </div>
                </div>
            )}

            <div className="ms-auto d-flex flex-wrap justify-content-end gap-2 flex-1" css={css`@media (min-width: 768px) { max-width: 50%; }`}>
                {prev && (
                    <Button
                        color="secondary"
                        outline
                        disabled={isUpdating}
                        onClick={() => navigateToCheckAction(prev.path)}
                        icon={faArrowLeft}
                    >
                        Back
                    </Button>
                )}
                {/* <div className=" me-2 d-inline-flex d-md-block">
                    {useGrid && <Button color="light" className="text-nowrap my-1" disabled={isUpdating} onClick={() => setUseGrid(false)}> <FontAwesomeIcon icon={faGripHorizontal} />  </Button>
                    }
                    {!useGrid && <Button color="light" className="text-nowrap my-1" disabled={isUpdating} onClick={() => setUseGrid(true)}> <FontAwesomeIcon icon={faGripVertical} />  </Button>
                    }
                </div> */}
                {useCopyPasteModal && (
                    <Button
                        color="secondary"
                        outline
                        disabled={isUpdating}
                        onClick={() => toggleBulkCopyPasteModal(true)}
                        icon={faPaste}
                    />
                )}

                {location?.enable_duplicate_fruit && (
                    <Button
                        color="secondary"
                        outline
                        disabled={isUpdating || check.avocados.length === 0}
                        onClick={() => duplicateFruit()}
                        icon={faCopy}
                    >
                        Duplicate {unitOfAccount}
                    </Button>
                )}

                <Button
                    color="primary"
                    outline
                    disabled={isUpdating}
                    onClick={onAddFruitButtonClick}
                    icon={faPlus}
                >
                    Add {unitOfAccount}
                </Button>

                {(isNextSummary || !next) && (
                    <Button
                        color="primary"
                        disabled={isUpdating}
                        onClick={onSummaryButtonClick}
                        icon={faList}
                    >
                        Summary
                    </Button>
                )}

                {!isNextSummary && next && (
                    <Button
                        color="primary"
                        disabled={isUpdating}
                        onClick={onNextButtonClick}
                    >
                        Next
                    </Button>
                )}
            </div>
        </div>
    );
};
